import { useViewCtx } from '../context/ViewContext'
import { legendRows, type LegendRow } from './legendRows'
import type { WaveSource } from './waveLabel'

/**
 * Единая легенда карты — одна плашка справа снизу (владелец 2026-08-07).
 *
 * Раньше подписи жили по всей карте: изотерма и фронты сверху по центру,
 * волнение и землетрясения справа снизу, Луна ещё ниже. Чем раскрашен кадр,
 * приходилось собирать взглядом по четырём углам.
 *
 * Состав строк считает `legendRows` — тот же список печатается на скачанной
 * картинке, поэтому экран и файл разойтись не могут. Состояние наложений
 * панель читает из общего контекста: включили инструмент в сайдбаре — строка
 * появилась, выключили — пропала.
 */
export interface DrawnFlags {
  isotherm?: boolean
  /** Легла ли ПОВЕРХНОСТНАЯ изотерма (своя сетка на 0 м). */
  isothermSurface?: boolean
  fronts?: boolean
  isobaths?: boolean
  waves?: boolean
  /** Промрайоны и запреты: что слой нарисовал и чья оговорка (регион кадра). */
  districts?: boolean
  bans?: boolean
  bansDisclaimerKey?: string | null
  bansEverywhere?: string[]
  /** Тип дна: классы, легшие в кадр (пусто — слой ничего не нарисовал). */
  substrate?: number[]
  navwarn?: string[]
  /** Сколько мест нейрокарты на карте. */
  nnMarks?: number
}

export function MapLegendPanel({
  earthquakes,
  waveSource,
  sstData,
  sstPartial,
  waveData,
  surfaceData,
  drawn,
  eezAttribution,
  overlays: shown,
}: {
  earthquakes: number
  waveSource?: WaveSource
  /** Есть ли сетка SST/волнения в кадре — иначе строка честно скажет «нет данных». */
  sstData?: boolean
  /** Поставка SST закрывает кадр не целиком — строка скажет «данные не на весь кадр». */
  sstPartial?: boolean
  waveData?: boolean
  /** Легло ли на карту хоть что-то по каждому инструменту: данные могут быть
   *  все, а порог — за пределами поля, и тогда строка скажет «в кадре нет». */
  /** Есть ли сетка ПОВЕРХНОСТИ в кадре: у неё своя правда о пустоте. */
  surfaceData?: boolean
  drawn?: DrawnFlags
  /** Полная ссылка на источник границ ИЭЗ. Требование лицензии CC-BY, и текст
   *  приезжает С ДАННЫМИ (`meta.attribution`), а не живёт здесь константой:
   *  подпись, отделённая от данных, пережила бы смену источника и стала бы
   *  ложной. `null` — слой выключен или ничего не нарисовал. */
  eezAttribution?: string | null
  /** Оверлеи, которые СЕЙЧАС на карте. Не передан — берём выбор человека из
   *  контекста, как обычно. Отличаются они только под серией: кадр замещает
   *  вид целиком, и панель обязана описывать кадр, а не то, что человек
   *  оставил включённым. */
  overlays?: string[]
}) {
  const {
    isotherm,
    isothermSurface,
    isothermSpread,
    fronts,
    frontsUnit,
    isobathStep,
    waveOverlay,
    overlays: userOverlays,
    date,
  } = useViewCtx()
  const overlays = shown ?? userOverlays
  const rows = legendRows({
    isotherm,
    isothermSurface,
    isothermSpread,
    fronts,
    frontsUnit,
    isobathStep,
    waveOverlay,
    waveSource,
    sstData,
    sstPartial,
    waveData,
    surfaceData,
    isothermDrawn: drawn?.isotherm,
    isothermSurfaceDrawn: drawn?.isothermSurface,
    frontsDrawn: drawn?.fronts,
    isobathsDrawn: drawn?.isobaths,
    waveDrawn: drawn?.waves,
    earthquakes,
    eez: overlays.includes('eez'),
    fishingDistricts: overlays.includes('fishing_districts'),
    districtsDrawn: drawn?.districts,
    bansDrawn: drawn?.bans,
    bansDisclaimerKey: drawn?.bansDisclaimerKey,
    bansEverywhere: drawn?.bansEverywhere,
    substrate: drawn?.substrate,
    navwarn: drawn?.navwarn,
    nnMarks: drawn?.nnMarks,
    moon: overlays.includes('moon'),
    date,
  })
  if (!rows.length) return null

  return (
    <div className="pointer-events-auto max-w-[330px] rounded-xl border border-hull bg-abyss/90 px-3 py-2 text-xs text-mist shadow-console backdrop-blur">
      <div className="flex flex-col gap-1.5">
        {rows.map(row => (
          <Row key={row.id} row={row} />
        ))}
      </div>
      {eezAttribution && (
        <div className="mt-1.5 border-t border-hull pt-1.5 text-[10px] leading-tight text-faint">
          {eezAttribution}
        </div>
      )}
    </div>
  )
}

/** Длина, после которой приписка перестаёт помещаться справа от текста.
 *  До сих пор приписками были счётчики и края шкалы («3», «0,5…≥6») — они
 *  короткие и стоят в строке. Оговорка источника — предложение, и втиснутая в
 *  `shrink-0` она растянула бы плашку на всю карту. */
const NOTE_INLINE_MAX = 24

function Row({ row }: { row: LegendRow }) {
  const wrap = (row.note?.length ?? 0) > NOTE_INLINE_MAX
  return (
    <div className="flex flex-col gap-0.5">
      <div className="flex items-center gap-2">
        <Mark row={row} />
        <span className="min-w-0 flex-1 leading-tight">{row.text}</span>
        {row.note && !wrap && <span className="tnum shrink-0 text-faint">{row.note}</span>}
      </div>
      {row.note && wrap && (
        <span className="pl-7 text-[10px] leading-tight text-faint">{row.note}</span>
      )}
    </div>
  )
}

function Mark({ row }: { row: LegendRow }) {
  const m = row.mark
  if (m.kind === 'line') {
    // Линия с ореолом — ровно как на карте: тёмная жила по светлой подложке.
    return (
      <span
        aria-hidden
        className="h-2 w-6 shrink-0 rounded-full"
        style={{
          background: `linear-gradient(to bottom,${m.casing} 0 30%,${m.color} 30% 70%,${m.casing} 70% 100%)`,
        }}
      />
    )
  }
  if (m.kind === 'dot') {
    return (
      <span
        aria-hidden
        className="h-2.5 w-2.5 shrink-0 rounded-full"
        style={{ background: m.color }}
      />
    )
  }
  if (m.kind === 'emoji') {
    return (
      <span aria-hidden className="shrink-0 text-base leading-none">
        {m.glyph}
      </span>
    )
  }
  // Рампа — такой же образец, как линия: тем же размером и на том же месте.
  // Края шкалы ушли в приписку справа (как счётчик у землетрясений).
  return <span aria-hidden className="h-2 w-6 shrink-0 rounded-full" style={{ background: m.gradient }} />
}
